import { useTranslation } from "react-i18next";
import { StyledColoredHeadline } from "../../../../../../../../../components/common";
import { DropdownField } from "../../../../../../../../../components/inputs";
import { usePartTypes } from "../../../../../../../useSelectOptions";
import { DraggableExerciseDropZone } from "./DraggableExerciseDropZones";
import {
  StyledTraningPartWrapper,
  TraningPartSelectWrapper,
  TraningPartsWrapper,
  ExerciseDragDropTitle,
  ExerciseDragDropLabel,
  StyledAddPart,
  StyledTraningPartsWrapper,
  StyledPartInputWrapper,
} from "../style";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useAppDispatch, useAppSelector } from "../../../../../../../../../store/config";
import { IUpdateWorkoutPayload, IWorkoutExercise, IWorkoutPart } from "../../../../../../../../../interfaces/store/workout-interface";
import { AbsoluteButton55, ErrorText, FullWidthFlex, NotFoundText, RelativeFlex } from "../../../../../../../../../components/common/styles";
import { ReactComponent as PlusIcon } from "../../../../../../../../../assets/icons/plus.svg";
import { useParams } from "react-router-dom";
import { Flex, Spin } from "antd";
import { DraggableExerciseCard } from "./DraggableExerciseCards";
import { TimePickerField } from "../../../../../../../../../components/inputs/TimePickerField";
import { updateWorkoutStart } from "../../../../../../../../../store/features/workout/workout-slice";
import dayjs from "dayjs";
import ExercisesOptions from "./ExercisesOptions";
import { resetExercises } from "../../../../../../../../../store/features/exercises/exercises-slice";

const WorkoutExercisesContent = ({ openNotification }: { openNotification: (val?: string | null) => void }) => {
  const { t } = useTranslation();
  const partTypes = usePartTypes();
  const { workoutId } = useParams<{ workoutId: string }>();
  const dispatch = useAppDispatch();

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [dndError, setDndError] = useState<string | null>(null);
  const [droppedExercises, setDroppedExercises] = useState<IWorkoutPart[]>([{ name: "", exercises: [], _id: "1" }]);
  const [partErrors, setPartErrors] = useState<{
    [key: string]: string;
  }>({});

  const { workout, loading, error, requestState, operation } = useAppSelector((state) => state.workout);

  useEffect(() => {
    if (requestState === "finish" && operation === "update") {
      setIsEditable(false);
    }
  }, [requestState, operation]);

  useEffect(() => {
    if (workout?.parts && workout.parts.length > 0) {
      const parts = workout.parts.map((part) => {
        return {
          ...part,
          duration: secondsToDayjs(part.duration as number),
          restTime: secondsToDayjs(part.restTime as number),
        };
      });
      setDroppedExercises(parts);
    }
  }, [workout]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  useEffect(() => {
    if (dndError) {
      openNotification(dndError);
      setDndError(null);
    }
  }, [dndError, openNotification]);

  useEffect(() => {
    return () => {
      dispatch(resetExercises());
    };
  }, []);

  const handleDrop = (partKey: string, exercise: IWorkoutExercise, targetIndex: number) => {
    if (!exercise) {
      return;
    }

    const exerciseWithSets = {
      ...exercise,
      sets: exercise.sets || [
        {
          type: "repetitions",
          value: 0,
        },
      ],
    };

    const exerciseNoSets = { ...exercise, sets: [] };

    setDroppedExercises((prevState) => {
      return prevState.map((part) => {
        if (part._id === partKey) {
          if (part.name === "") {
            setDndError(t("Select part type before adding exercises"));
            return part;
          } else if (part.name === "superSet" && part.exercises.length >= 2) {
            setDndError(t("You can add only 2 exercises to super set"));
            return part;
          } else if (part.name === "tripleSet" && part.exercises.length >= 3) {
            setDndError(t("You can add only 3 exercises to triple set"));
            return part;
          }

          const updatedPartExercises = [...part.exercises, part.name === "TABATA" ? exerciseNoSets : exerciseWithSets];

          return {
            ...part,
            exercises: updatedPartExercises,
          };
        }
        return part;
      });
    });
  };

  const handleMove = (partKey: string, dragIndex: number, hoverIndex: number) => {
    setDroppedExercises((prevState) => {
      const updatedParts = [...prevState];
      const currentPart = updatedParts.find((part) => part._id === partKey);
      if (!currentPart) {
        return prevState;
      }

      const updatedExercises = [...currentPart.exercises];
      const [moveExercise] = updatedExercises.splice(dragIndex, 1);
      updatedExercises.splice(hoverIndex, 0, moveExercise);
      currentPart.exercises = updatedExercises;

      return updatedParts;
    });
  };

  const handleRemove = (partKey: string, index: number): void => {
    setDroppedExercises((prevState) =>
      prevState.map((part) => {
        if (part._id === partKey) {
          return {
            ...part,
            exercises: part.exercises.filter((_, i) => i !== index),
          };
        }
        return part;
      })
    );
  };

  const validateParts = (parts: IWorkoutPart[]) => {
    const newPartErrors: { [key: string]: string } = {};

    for (const part of parts) {
      const duration = part.duration as dayjs.Dayjs;
      const restTime = part.restTime as dayjs.Dayjs;

      if (partsWithDuration.includes(part.name) && (!duration || (duration?.minute() === 0 && duration?.second() === 0))) {
        newPartErrors[part._id || ""] = t("Enter total time for section");
      } else if (part.name === "EMOM" && !(duration?.minute() % part.exercises.length === 0)) {
        newPartErrors[part._id || ""] = t(t("Invalid part, exercise count does not match total time"));
      } else if (partsWithRounds.includes(part.name) && (!part.rounds || part.rounds === 0)) {
        newPartErrors[part._id || ""] = partsWithSuperSets.includes(part.name) ? t("Enter number of sets") : t("Enter number of rounds");
      } else if (partsWithRestTime.includes(part.name) && (!restTime || (restTime?.minute() === 0 && restTime?.second() === 0))) {
        if (!partsWithOptionalRestTime.includes(part.name)) {
          newPartErrors[part._id || ""] = partsWithSuperSets.includes(part.name) ? t("Enter rest time between sets") : t("Enter rest time between rounds");
        }
      }
    }

    if (Object.keys(newPartErrors).length > 0) {
      setPartErrors(newPartErrors);
      setDndError(t("There are invalid parts"));
      return false;
    }

    setPartErrors({});
    return true;
  };

  const handleSave = () => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const parts = droppedExercises.filter((part) => part.exercises.length > 0);

    const isValid = validateParts(parts);
    if (!isValid) {
      return;
    }

    const modifiedParts = parts.map((part) => {
      const durationInSeconds = dayjsToSeconds(part.duration as dayjs.Dayjs);
      const restTimeInSeconds = dayjsToSeconds(part.restTime as dayjs.Dayjs);

      return {
        name: part.name,
        duration: durationInSeconds,
        restTime: restTimeInSeconds,
        rounds: part.rounds,
        exercises: part.exercises.map((exercise) => {
          return {
            restTime: exercise.restTime,
            originalExercise: exercise.originalExercise || exercise._id,
            name: exercise.name,
            description: exercise.description,
            image: exercise.image,
            muscles: exercise.muscles,
            alternativeExercises: exercise.alternativeExercises,
            sets: exercise.sets || [],
            video: exercise.video,
          };
        }),
      };
    });

    const workoutParts = {
      workout: { parts: modifiedParts },
      workoutId: workoutId,
    } as IUpdateWorkoutPayload;

    if (parts.length === 0) {
      setDroppedExercises([{ name: "", exercises: [], _id: "1" }]);
    }
    dispatch(updateWorkoutStart(workoutParts));
  };

  const addSection = () => {
    setDroppedExercises((prevState) => [
      ...prevState,
      {
        _id: `${prevState.length + 1}`,
        name: "",
        exercises: [],
      },
    ]);
  };

  const secondsToDayjs = (seconds: number | undefined) => {
    if (!seconds) {
      return dayjs().set("minute", 0).set("second", 0);
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return dayjs().set("minute", minutes).set("second", remainingSeconds);
  };

  const dayjsToSeconds = (time: dayjs.Dayjs | undefined) => {
    return time ? dayjs(time).hour() * 3600 + dayjs(time).minute() * 60 + dayjs(time).second() : undefined;
  };

  const handlePartNameChange = (value: string, part: IWorkoutPart) => {
    setDroppedExercises((prevState) =>
      prevState.map((currPart) => {
        if (currPart._id === part._id) {
          let updatedExercises = currPart.exercises;

          if (partsWithExercisesWithNoSets.includes(value)) {
            updatedExercises = updatedExercises.map((exercise) => {
              if (exercise.sets && exercise.sets.length > 0) {
                return {
                  ...exercise,
                  sets: [],
                };
              }
              return exercise;
            });
          } else if (partsWithExercisesWithOneSet.includes(value)) {
            updatedExercises = updatedExercises.map((exercise) => {
              if (exercise.sets?.length !== 1) {
                return {
                  ...exercise,
                  sets:
                    exercise.sets?.length > 1
                      ? exercise.sets.slice(0, 1)
                      : [
                          {
                            type: "repetitions",
                            value: 0,
                          },
                        ],
                };
              }
              return exercise;
            });
          } else {
            updatedExercises = updatedExercises.map((exercise) => {
              return {
                ...exercise,
                sets:
                  exercise.sets?.length > 0
                    ? exercise.sets
                    : [
                        {
                          type: "repetitions",
                          value: 0,
                        },
                      ],
              };
            });
          }
          if (value === "superSet") {
            updatedExercises = updatedExercises.slice(0, 2);
          }
          if (value === "tripleSet") {
            updatedExercises = updatedExercises.slice(0, 3);
          }

          if (!partsWithExercisesRestTime.includes(value)) {
            updatedExercises = updatedExercises.map((exercise) => {
              if (exercise.restTime !== undefined) {
                return { ...exercise, restTime: undefined };
              }
              return exercise;
            });
          }

          return {
            ...currPart,
            name: value,
            exercises: updatedExercises,

            ...(!partsWithDuration.includes(value) ? { duration: undefined } : {}),

            ...(!partsWithRounds.includes(value) ? { rounds: undefined } : {}),

            ...(!partsWithRestTime.includes(value) ? { restTime: undefined } : {}),
          };
        }
        return currPart;
      })
    );
  };

  const handleRoundsChange = (value: number, part: IWorkoutPart) => {
    setDroppedExercises((prevState) =>
      prevState.map((currPart) => {
        if (currPart._id === part._id) {
          return {
            ...part,
            rounds: value,
          };
        }
        return currPart;
      })
    );
  };

  const handleRoundsRestTimeChange = (value: dayjs.Dayjs | undefined, part: IWorkoutPart) => {
    setDroppedExercises((prevState) =>
      prevState.map((currPart) => {
        if (currPart._id === part._id) {
          return {
            ...part,
            restTime: value,
          };
        }
        return currPart;
      })
    );
  };

  const handleDurationChange = (value: dayjs.Dayjs | undefined, part: IWorkoutPart) => {
    setDroppedExercises((prevState) =>
      prevState.map((currPart) => {
        if (currPart._id === part._id) {
          return {
            ...part,
            duration: value,
          };
        }
        return currPart;
      })
    );
  };

  const droppedExercisesByPart = isEditable ? [...droppedExercises].reverse() : droppedExercises;

  const partsWithDuration = ["AMRAP", "TABATA", "EMOM"];
  const partsWithRounds = ["activation", "mobility", "rounds", "core", "stretches", "dynamicWarmup", "metcon", "superSet", "tripleSet", "powerBlock", "stations"];
  const partsWithRestTime = ["activation", "mobility", "rounds", "core", "stretches", "dynamicWarmup", "superSet", "tripleSet", "AMRAP", "metcon", "powerBlock"];
  const partsWithOptionalRestTime = ["AMRAP", "metcon"];
  const partsWithSuperSets = ["superSet", "tripleSet"];

  const partsWithExerciseDuration = ["stations"];
  const partsWithExerciesRestTime = ["stations"];

  const partsWithExercisesRestTime = ["dropSet", "training", "intervals", "finisher", "running", "sprint"];

  const partsWithExercisesWithOneSet = ["EMOM", "AMRAP", "superSet", "warmUp", "stretches", "dynamicWarmup", "core", "mobility", "activation", "tripleSet", "metcon"];

  const partsWithExercisesWithNoSets = ["TABATA", "stations"];

  const showNoExercises = !isEditable && !loading && droppedExercises.length === 1 && droppedExercises[0]._id === "1";

  return (
    <FullWidthFlex vertical>
      <Spin spinning={loading}>
        <RelativeFlex>
          <AbsoluteButton55 onClick={handleSave}>{isEditable ? t("Save") : t("Edit")}</AbsoluteButton55>
        </RelativeFlex>
        {showNoExercises ? (
          <NotFoundText>{t("No exercises")}</NotFoundText>
        ) : (
          <>
            <DndProvider backend={HTML5Backend}>
              <Flex justify="space-between">
                <Flex vertical style={{ width: "67%" }} justify="space-between">
                  <StyledTraningPartsWrapper isEditable={isEditable}>
                    {droppedExercisesByPart.map((part, partIndex) => {
                      const index = isEditable ? droppedExercises.length - 1 - partIndex : partIndex;
                      return (
                        <StyledTraningPartWrapper key={index}>
                          <TraningPartSelectWrapper>
                            <StyledColoredHeadline key={index}>
                              {t("Part")} {index + 1}
                            </StyledColoredHeadline>
                            {partErrors[part._id || ""] && <ErrorText>{partErrors[part._id || ""]}</ErrorText>}
                            <Flex gap={20}>
                              <StyledPartInputWrapper>
                                <DropdownField label={t("Part Type")} isEditable={isEditable} options={partTypes} value={part.name} onChange={(val) => handlePartNameChange(val, part)} />
                              </StyledPartInputWrapper>

                              {partsWithExerciseDuration.includes(part.name) && (
                                <TimePickerField label={t("Total Time for Exercise")} isEditable={isEditable} value={part.duration} onChange={(val) => handleDurationChange(val, part)} />
                              )}

                              {partsWithExerciesRestTime.includes(part.name) && (
                                <TimePickerField label={t("Rest Time Between Exercises")} isEditable={isEditable} value={part.restTime} onChange={(val) => handleRoundsRestTimeChange(val, part)} />
                              )}

                              {partsWithDuration.includes(part.name) && (
                                <TimePickerField label={t("Total Time for Section")} isEditable={isEditable} value={part.duration} onChange={(val) => handleDurationChange(val, part)} />
                              )}
                              {partsWithRounds.includes(part.name) && (
                                <StyledPartInputWrapper>
                                  <DropdownField
                                    label={partsWithSuperSets.includes(part.name) ? t("Number of Sets") : t("Number of Rounds")}
                                    isEditable={isEditable}
                                    options={Array.from(Array(50).keys()).map((i) => ({
                                      label: (i + 1).toString(),
                                      value: i + 1,
                                    }))}
                                    value={part.rounds}
                                    onChange={(val) => handleRoundsChange(val, part)}
                                  />
                                </StyledPartInputWrapper>
                              )}
                              {partsWithRestTime.includes(part.name) && (
                                <TimePickerField
                                  label={partsWithSuperSets.includes(part.name) ? t("Sets Rest Time") : t("Rounds Rest Time")}
                                  limitedSecondsOptions={true}
                                  isEditable={isEditable}
                                  value={part.restTime}
                                  onChange={(val) => handleRoundsRestTimeChange(val, part)}
                                />
                              )}
                            </Flex>
                          </TraningPartSelectWrapper>
                          <TraningPartsWrapper>
                            <DraggableExerciseDropZone partKey={part._id} onDrop={handleDrop}>
                              {part.exercises.length > 0 ? (
                                part.exercises.map((exercise, index) => (
                                  <DraggableExerciseCard
                                    key={index}
                                    exercise={exercise}
                                    part={part}
                                    index={index}
                                    moveItem={handleMove}
                                    handleRemove={handleRemove}
                                    isEditable={isEditable}
                                    setDroppedExercises={setDroppedExercises}
                                  />
                                ))
                              ) : (
                                <>
                                  <ExerciseDragDropTitle>{t("Exercises")}</ExerciseDragDropTitle>
                                  <ExerciseDragDropLabel>{t("Drag exercises from the list on the left to the box")}</ExerciseDragDropLabel>
                                </>
                              )}
                            </DraggableExerciseDropZone>
                          </TraningPartsWrapper>
                        </StyledTraningPartWrapper>
                      );
                    })}
                  </StyledTraningPartsWrapper>
                  {isEditable && (
                    <StyledAddPart vertical gap={5} align="center" onClick={addSection}>
                      <PlusIcon />
                      <StyledColoredHeadline>{t("Add part")}</StyledColoredHeadline>
                    </StyledAddPart>
                  )}
                </Flex>
                <ExercisesOptions isEditable={isEditable} />
              </Flex>
            </DndProvider>
          </>
        )}
      </Spin>
    </FullWidthFlex>
  );
};

export default WorkoutExercisesContent;
